<template>
	<div class="w-full h-full" ref="mapContainer"></div>
	<RegionFloatingBox v-if="miniMarkers && selectedRegion != null" :region="selectedRegion"/>
</template>

<script setup>
/* eslint no-unused-vars: 0 */
import 'maplibre-gl/dist/maplibre-gl.css'
import { Map as MapLibreMap, Marker, LngLatBounds } from 'maplibre-gl'

import { ref, shallowRef, onMounted, onUnmounted, markRaw, computed, watch, defineExpose } from 'vue'
import { useColorMode, useDebounceFn } from '@vueuse/core'

import RegionFloatingBox from '@/components/RegionFloatingBox.vue'

import { StationsData } from '@/state/StationsData.js'
import { RegionsData } from '@/state/RegionsData.js'
import { SelectedStation } from '@/state/SelectedStation.js'
import { getStationIconElement } from '@/modules/StationIcon.js'

const colorMode = useColorMode()

//const styles = {
//	light: 'dataviz-light',
//	dark: 'dataviz-dark'
//}

//const mapStyle = computed(() => {
//	return `https://api.maptiler.com/maps/${styles[colorMode.value]}/style.json?key=HJbH3YPrb5E2BgjDfQ68`
//})

const styles = {
	light: 'white', 
	dark: 'dark'
}

const mapStyle = computed(() => {
	return `https://api.protomaps.com/styles/v5/${styles[colorMode.value]}/uk.json?key=ae3daa180d8ce285`
})

const mapContainer = shallowRef(null)
const map = shallowRef(null)

// Little markers and show region on zoom out
const miniMarkers = ref(false)

// Region under the mouse
const selectedRegion = ref(null)

// To store existing markers and update them
const markerMap = new Map()

const stationsToShow = computed(() => {
	const stations = StationsData.stations

	if (stations == null || stations.length < 1) return []

	return stations
		.filter(s => s.latitude != null && s.longitude != null)
		.filter(s => s.latitude != "" && s.longitude != "")
})

function initMap() {
	if (!mapContainer.value) {
		console.error("mapContainer is not ready yet!")
		return
	}

	const initialState = { lat: 50.44, lng: 30.52, zoom: 10 }
	const maxBounds = [
		[12, 35], // [west, south]
		[52, 60]  // [east, north]
	]

	map.value = markRaw(new MapLibreMap(
		{
			container: mapContainer.value,
			style: mapStyle.value,
			center: [initialState.lng, initialState.lat],
			zoom: initialState.zoom,
			// attributionControl: false,
			maxZoom: 14,
			minZoom: 3,
			maxBounds: maxBounds
		}
	))

	map.value.on("zoom", () => {
		const currentMini = map.value.getZoom() < 8

		if (currentMini != miniMarkers.value) {
			updateMiniMarkers(currentMini)
			updateRegionsVisibility(currentMini)
			miniMarkers.value = currentMini
		}
	})

	map.value.on("move", () => {
		updateMarkersVisibility()
	})

	// Get last map position
	const lastPositionJSON = localStorage.getItem('lastPosition')

	if (lastPositionJSON != null) 
	{ 
		const lastPosition = JSON.parse(lastPositionJSON) 
		map.value.setCenter(lastPosition.location)
		map.value.setZoom(lastPosition.zoom)
	} 

	map.value.on("moveend", () => {
		const position = { location: map.value.getCenter(), zoom: map.value.getZoom() }
		localStorage.setItem('lastPosition', JSON.stringify(position))
	})

	// Add regions GeoJSON
	map.value.on("load", () => addRegionsToMap())

	// Show popup of region
	map.value.on('mousemove', 'regions-fill', (e) => {
		const features = map.value.queryRenderedFeatures(e.point)

		if (features.length > 0) {
			const feature = features[0]
			const regionName = feature.properties.name

			const region = RegionsData.regions.find(r => r.name == regionName)

			selectedRegion.value = { name: region.name, level: region.level}
		} else {
			selectedRegion.value = null
		}
	})

	map.value.on('mouseleave', 'regions-fill', () => {
		selectedRegion.value = null
	})
}

let lastStationsTimestamps = new Map()

function updateMarkers() {
	const stations = stationsToShow.value
	const newIds = new Set(stations.map(s => s.id))

	console.log("Updating map data\n Stations to show: ", stations.length)

	// Remove markers for stations that are no longer visible
	for (const [id, marker] of markerMap.entries()) {
		if (!newIds.has(id)) {
			marker.remove()
			markerMap.delete(id)
		}
	}

	for (const station of stations) {
		let marker = markerMap.get(station.id)

		// Create marker
		if (!marker) {
			const el = document.createElement('div')
			el.className = 'station-marker'
			el.dataset.stationId = station.id

			if (station.type != "outdoor" || station.service) el.appendChild(getStationIconElement(station))
			
			el.addEventListener('click', () => SelectedStation.set(station))

			marker = new Marker({ element: el })
				.setLngLat([station.longitude, station.latitude])
				.addTo(map.value)

			markerMap.set(station.id, marker)
		}

		// Set color and z-index
		const el = marker.getElement()
		el.style.setProperty('--color-uaqi-level', `var(--color-uaqi-${station.current_level})`)
		el.style.setProperty('--marker-z-index', station.current_level)

		// Add animation if data updated
		const lastTimestamp = lastStationsTimestamps.get(station.id)
		if (
			miniMarkers.value == false &&
			(lastTimestamp == null || new Date(lastTimestamp) < new Date(station.last_online))
		) {
			el.classList.add("update")
			setTimeout(() => { el.classList.remove("update") }, 500)
		}

		lastStationsTimestamps.set(station.id, station.last_online)
	}

	// Update selected as we might remove necessary styles
	updateSelectedStationMarker()
}

function updateSelectedStationMarker() {
	const selected = SelectedStation.station

	for (const [id, marker] of markerMap.entries()) {
		const el = marker.getElement()

		if (selected && id === selected.id) {
			el.classList.add('selected')
		} else {
			el.classList.remove('selected')
		}
	}
}

function updateMiniMarkers(mini) {
	for (const [id, marker] of markerMap.entries()) {
		const el = marker.getElement()

		if (mini) {
			el.classList.add('mini')
		} else {
			el.classList.remove('mini')
		}
	}
}

async function addRegionsToMap() {
	console.log("Regions adding")

	if (RegionsData.isUpdateNeeded()) await RegionsData.getUpdated()

	map.value.addSource('regions', {
        'type': 'geojson',
        'data': 'ukraine_regions.json' // geojson // fetchRegionsGeoJSON()
	})

	map.value.addLayer({
		id: 'regions-fill',
		type: 'fill',
		source: 'regions',
		paint: {
			'fill-color': '#666',
			'fill-opacity': 0.3
		},
		layout: {
			'visibility': miniMarkers.value ? 'visible' : 'none'
		}
	})

	map.value.addLayer({
		id: 'regions-outline',
		type: 'line',
		source: 'regions',
		paint: {
			'line-color': colorMode.value == 'dark' ? "#dadada" : "#909090",
			'line-width': 1
		},
		layout: {
			'visibility': miniMarkers.value ? 'visible' : 'none'
		}
	})

	updateRegions()
}

function getLevelColorFromCss(level) {
	return window.getComputedStyle(document.body).getPropertyValue(`--color-uaqi-${level}`);
}

// Update levels of regions
function updateRegions() {
	const matchExpression = ['match', ['get', 'name']];

	RegionsData.regions.forEach(region => {
		matchExpression.push(region.name, getLevelColorFromCss(region.level));
	});

	// Default color
	matchExpression.push('gray');

	map.value.setPaintProperty('regions-fill', 'fill-color', matchExpression);
}

// Show/hide regions 
function updateRegionsVisibility(show) {
	if (map.value.style && map.value.style.loaded()) {
		console.log("Updating regions visibility", show)
		map.value.setLayoutProperty('regions-fill', 'visibility', show ? 'visible' : 'none');
		map.value.setLayoutProperty('regions-outline', 'visibility', show ? 'visible' : 'none');
	} else {
		setTimeout(() => { 
			console.warn("DELAYED updateRegionsVisibility"); 
			updateRegionsVisibility(show) 
		}, 500)
	}
}

function getExtendedBounds(bounds, percentage) {
	const sw = bounds.getSouthWest();
	const ne = bounds.getNorthEast();

	const width = ne.lng - sw.lng
	const height = ne.lat - sw.lat

	const exLon = width * (percentage / 2)
	const exLat = height * (percentage / 2)

	return new LngLatBounds(
		[sw.lng - exLon, sw.lat - exLat],
		[ne.lng + exLon, ne.lat + exLat]
	);
}

const updateMarkersVisibility = useDebounceFn(() => {
	const bounds = getExtendedBounds(map.value.getBounds(), 0.2)

	for (const [id, marker] of markerMap.entries()) {
		const position = marker.getLngLat()

		if (position == null) continue

		const show = bounds.contains(position)
		
		marker.getElement().style.visibility = show ? "visible" : "hidden" 
	}
}, 10, { maxWait: 100 })

onMounted(async () => {
	initMap()

	// Update data if needed
	if (StationsData.isUpdateNeeded()) await StationsData.update()

	// Set in use to auto update stations
	StationsData.setInUse("map")

	updateMarkers()
})

onUnmounted(() => {
	map.value?.remove()
	StationsData.unsetInUse("map")
})

// Update markers on data change
watch(stationsToShow, () => {
	updateMarkers()
})

// Update selected station
watch(SelectedStation, () => {
	updateSelectedStationMarker()
}, { immediate: true })

// Update style
watch(colorMode, () => {
	map.value.setStyle(mapStyle.value)
	
	// The event to be sure that the style is loaded
	map.value.once('styledata', () => { addRegionsToMap() })
})

function panToLocation(location) {
	map.value.flyTo({
		center: [
			location.lng,
			location.lat
		],
		zoom: 11
	})
}

defineExpose({ panToLocation });
</script>

<style>
.station-marker {
	background-color: var(--color-uaqi-level); 

	z-index: var(--marker-z-index);
	
	border-radius: 100%; 
	border: 4px solid var(--color-muted-background);
	
	width: 28px; 
	height: 28px;

	color: white;

	display: flex;
	align-items: center;
	justify-content: center;

	cursor: pointer;

	overflow: hidden;

	will-change: width, height, border-width, border-color;

	transition: width 0.3s ease,
				height 0.3s ease,
				border-width 0.3s ease,
				border-color 0.3s ease;
}

.station-marker > svg { 
	height: 1em;
	/* color: var(--color-foreground) */
}

.station-marker.selected {
	border-color: var(--color-foreground);
	z-index: 10;

	/* box-shadow: 1px 1px 20px 10px color-mix(in lab, var(--color-muted-background), #0000 60%); */
}

.station-marker.update {
	width: 32px;
	height: 32px;
}

.station-marker.mini {
	width: 10px; 
	height: 10px;

	border-width: 0px;
}

.station-marker.mini > svg { 
	display: none;
}
</style>
